import React, { forwardRef } from 'react'
import styled, { css } from 'styled-components'
import { string, object, bool, node } from 'prop-types'
import { rgba, stripUnit } from 'polished'
import pick from 'lodash/pick'

import { white, black, grey, borderColor, red, greyLight, offWhite } from 'styles/colors'
import { animationTime, animationCurve, inputHeight, inputHeightMobile, borderRadius } from 'styles/global'
import { fontSizes, fontWeights } from 'styles/typography'
import Icon from 'components/Icon'
import Label from 'components/Label'
import * as spacing from 'styles/spacing'
import { media } from 'styles/media'

const Container = styled.div`
  display: block;
  width: 100%;
`

const StyledLabel = styled(Label)`
  position: static;

  ${props =>
    props.isFloating &&
    css`
      position: relative;
      z-index: 10;
      margin-top: -13px;
      font-weight: ${fontWeights.regular};
      font-size: inherit;
      color: ${rgba(black, 0.6)};
      margin-bottom: 0;
      top: 31px;
      pointer-events: none;
      transition: transform ${animationTime} ${animationCurve};
      transform-origin: top left;

      ${media.medium`
        top: 35px;
      `};
    `}

  ${props =>
    props.isFloating &&
    props.isFilled &&
    css`
      transform: translateY(-19px) scale(0.8);

      ${media.medium`
        transform: translateY(-23px) scale(0.8);
      `};
    `}
`

const Wrap = styled.div`
  position: relative;
  box-shadow: 0px 4px 14px rgba(0, 0, 79, 0.05);
  border-radius: ${borderRadius};

  ${props =>
    props.isFloating &&
    css`
      box-shadow: none;
      border-radius: 0;
    `}

  ${props =>
    props.narrow &&
    css`
      max-width: 80px;
    `}
`

const Field = styled.input`
  height: ${inputHeightMobile};
  border-radius: ${borderRadius};
  box-shadow: inset 0 0 0 1px ${borderColor};
  background-color: ${white};
  width: 100%;
  resize: none;
  color: ${grey};
  vertical-align: top;
  outline: 0;
  padding: 0 ${spacing.medium};
  transition: all ${animationTime} ${animationCurve};

  ${media.medium`
    height: ${inputHeight};
  `};

  ${props =>
    props.icon &&
    `
    padding-left: 46px;
  `}

  ${props =>
    props.addon &&
    `
    padding-right: ${spacing.xxxLarge};
  `}

  ${props =>
    props.error &&
    `
    box-shadow: inset 0 0 0 1px ${red};
  `}

  &::placeholder {
    color: rgba(grey, 0.4);
  }

  &:focus {
    background-color: ${white};
    box-shadow: inset 0 0 0 1px ${black};
  }

  &[disabled] {
    color: ${greyLight};
    pointer-events: none;
    background-color: ${rgba(offWhite, 0.75)};
    box-shadow: inset 0 0 0 1px ${borderColor};
    user-select: none;
    opacity: 1; /* Overwrite iOS styles */
  }

  ${props =>
    props.isFloating &&
    `
    color: ${black};
    box-shadow: inset 0 -1px 0 0 ${greyLight};
    border-radius: 0;
    padding-left: 0;
    padding-right: 0;

    &:focus {
      box-shadow: inset 0 -1px 0 0 ${black};
    }

    &[disabled] {
      color: ${greyLight};
      pointer-events: none;
      user-select: none;
      opacity: 0.7; /* Overwrite iOS styles */
      box-shadow: inset 0 -1px 0 0 ${greyLight};
      background-color: transparent;
    }
  `}

  ${props =>
    props.error &&
    props.isFloating &&
    `
    box-shadow: inset 0 -1px 0 0 ${red};
  `}
`

const Textarea = styled(Field)`
  padding: ${stripUnit(spacing.medium) - 2 + 'px'} ${spacing.medium};
  min-height: 160px;
  resize: vertical;

  ${media.small`
    min-height: 200px;
  `};

  ${media.medium`
    min-height: 240px;
  `};

  ${media.large`
    min-height: 260px;
  `};
`

const FieldIcon = styled.div`
  display: flex;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: ${stripUnit(spacing.medium) * 0.75 + 'px'};
  pointer-events: none;
  color: ${greyLight};
  transition: color ${animationTime} ${animationCurve};

  ${Field}:focus + & {
    color: ${black};
  }
`

const Addon = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: ${spacing.medium};
  color: ${black};
  font-size: ${fontSizes.small};

  ${props =>
    props.isFloating &&
    `
    right: 0;
  `}
`

const ErrorMessage = styled.small`
  color: ${red};
  display: block;
  margin-top: ${spacing.small};
  font-size: ${fontSizes.tiny};
  text-align: left;

  ${props =>
    props.isFloating &&
    `
    margin-top: ${spacing.tiny};
  `}
`

const Hint = styled.small`
  color: ${greyLight};
  display: block;
  margin-top: ${spacing.small};
  font-size: ${fontSizes.tiny};
  text-align: left;
`

const Input = forwardRef(
  ({ label, hint, narrow, placeholder, addon, icon, textarea, meta = {}, isFloating, field, form, ...rest }, ref) => {
    const fieldProps = field || pick(rest, ['value', 'onChange', 'onBlur', 'name'])
    const isFilled = fieldProps.value && fieldProps.value.length > 0
    const touched = meta.touched || form?.touched[fieldProps.name]
    const error = meta.error || form?.errors[fieldProps.name]

    return (
      <Container>
        {label && (
          <StyledLabel isFloating={isFloating} isFilled={isFilled}>
            {label}
          </StyledLabel>
        )}
        <Wrap narrow={narrow} isFloating={isFloating}>
          {!textarea && (
            <Field
              ref={ref}
              icon={icon}
              addon={addon}
              isFloating={isFloating}
              placeholder={isFloating ? null : placeholder}
              error={error && touched}
              {...fieldProps}
              {...rest}
              value={fieldProps.value || ''}
            />
          )}
          {textarea && <Textarea ref={ref} as="textarea" error={error && touched} {...fieldProps} {...rest} />}
          {icon && (
            <FieldIcon>
              <Icon icon={icon} />
            </FieldIcon>
          )}
          {addon && <Addon isFloating={isFloating}>{addon}</Addon>}
        </Wrap>
        {hint && <Hint>{hint}</Hint>}
        {touched && error && <ErrorMessage isFloating={isFloating}>{error}</ErrorMessage>}
      </Container>
    )
  }
)

Input.propTypes = {
  label: string,
  icon: string,
  hint: string,
  narrow: bool,
  textarea: bool,
  isFloating: bool,
  placeholder: string,
  field: object,
  meta: object,
  addon: node,
  form: object
}

export default Input
